'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment, useState, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import * as CustomPropTypes from '../../../constants/PropTypes';
import { connect } from 'react-redux';
import { useLocation } from 'react-router-dom';
import PortalIdParser from 'PortalIdParser';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import Loadable from 'UIComponents/decorators/Loadable';
import UIButton from 'UIComponents/button/UIButton';
import UILink from 'UIComponents/link/UILink';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import UIModal from 'UIComponents/dialog/UIModal';
import UIDialogHeader from 'UIComponents/dialog/UIDialogHeader';
import UIDialogCloseButton from 'UIComponents/dialog/UIDialogCloseButton';
import UIDialogBody from 'UIComponents/dialog/UIDialogBody';
import H2 from 'UIComponents/elements/headings/H2';
import { useAppInstallability } from 'ecosystem-marketplace-core/hooks/useAppInstallability';
import * as AppUninstallableReasons from 'ecosystem-marketplace-core/constants/installability/AppUninstallableReasons';
import { handleConnectError } from '../../../actions/ConnectActions';
import { getEventSelectorId } from '../../../utils/analytics';
import useShortlinkParams from '../../../utils/hooks/useShortlinkParams';
import { getIsStandardConnection, getIsHomemadeExternalConnection, getIsHomemadeModalConnection } from '../../../utils/offerings';
import { getFormattedUrl, getConnectedAppsUrl, getAppShortlink } from '../../../utils/urls';
const AsyncRequestPermissionsModal = Loadable({
  loader: () => import(
  /* webpackChunkName: 'request-permissions-modal' */
  './RequestPermissionsModal')
});
const AsyncIntegrationsConnectButton = Loadable({
  loader: () => import(
  /* webpackChunkName: 'integrations-connect-button' */
  'ui-addon-integrations-directory-panel/UIIntegrationsConnectButton')
});
const AsyncIntegrationsConnectModal = Loadable({
  loader: () => import(
  /* webpackChunkName: 'integrations-connect-modal' */
  'ui-addon-integrations-directory-panel/UIIntegrationsConnectModal')
});
const AsyncDataSyncConnectButton = Loadable({
  loader: () => import(
  /* webpackChunkName: 'data-sync-connect-button' */
  'ui-universal-auth/DataSyncAuthButton')
});
const keyPath = 'appMarketplace.detail.header.connectAppButton';
const connectButtonKeyPath = `${keyPath}.text`;

const ConnectButton = ({
  appId,
  appName,
  appUrl,
  connectionType,
  isCustomer,
  isDataSyncApp,
  installability,
  listingId,
  onConnectButtonClick,
  onConnectFailed,
  onTrackEvent,
  onTrackInteraction,
  onTrackPageview,
  sticky
}) => {
  const {
    pathname
  } = useLocation();
  const shortlinkParams = useShortlinkParams();
  const [openIframeModal, setOpenIframeModal] = useState(false);
  const [openPermissionsModal, setOpenPermissionsModal] = useState(false);
  const standardConnection = getIsStandardConnection(connectionType);
  const homemadeExternalConnection = getIsHomemadeExternalConnection(connectionType);
  const homemadeModalConnection = getIsHomemadeModalConnection(connectionType);
  const {
    isAppInstallable,
    appUninstallableReason,
    appInstallabilityExtras
  } = useAppInstallability({
    installability
  });
  const handleTrackInteraction = useCallback(action => {
    onTrackInteraction(action, 'header', appName);
  }, [appName, onTrackInteraction]);
  const tooltip = useMemo(() => {
    switch (appUninstallableReason) {
      case AppUninstallableReasons.ALREADY_INSTALLED:
        return /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: `${keyPath}.tooltip.installed_jsx`,
          elements: {
            Link: UILink
          },
          options: {
            href: getConnectedAppsUrl(true)
          }
        });

      case AppUninstallableReasons.MISSING_HUB_SCOPES:
        // TODO placeholder until PQL modal
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${keyPath}.tooltip.missingHubScopes`
        });

      case AppUninstallableReasons.INCOMPATIBLE_INSTALLS:
        return /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${keyPath}.tooltip.incompatibleInstalls`,
          options: {
            count: appInstallabilityExtras.size,
            installs: I18n.formatList(appInstallabilityExtras.toArray())
          }
        });

      default:
        {
          if (!isAppInstallable) {
            return /*#__PURE__*/_jsx(FormattedMessage, {
              message: `${keyPath}.tooltip.missingHubUserInstallable`
            });
          }

          return null;
        }
    }
  }, [appUninstallableReason, appInstallabilityExtras, isAppInstallable]); // Public connect button that redirects to portal picker

  if (!isCustomer) {
    return /*#__PURE__*/_jsx(UIButton, {
      "data-hs-analytics-id": getEventSelectorId('HEADER-CONNECT'),
      size: sticky ? 'small' : 'default',
      use: "primary",
      href: getAppShortlink(pathname, shortlinkParams),
      onClick: onConnectButtonClick,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: `${keyPath}.publicText`
      })
    });
  }

  if (appUninstallableReason === AppUninstallableReasons.MISSING_USER_PERMISSIONS) {
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(UIButton, {
        size: sticky ? 'small' : 'default',
        use: "primary",
        onClick: () => {
          setOpenPermissionsModal(true);
          handleTrackInteraction('click request install app permission button');
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: `${keyPath}.requestToConnect`
        })
      }), openPermissionsModal && /*#__PURE__*/_jsx(AsyncRequestPermissionsModal, {
        listingId: listingId,
        missingHubUserRoles: appInstallabilityExtras,
        onClose: () => setOpenPermissionsModal(false),
        onTrackEvent: onTrackEvent,
        onTrackPageview: onTrackPageview
      })]
    });
  }

  const handleConnectionComplete = payload => {
    const redirectUrl = payload && payload.meta && payload.meta.redirectUrl;
    window.location.href = redirectUrl || getConnectedAppsUrl(true);
  }; // Simple button and disabled button for integrations


  if (tooltip || standardConnection || homemadeExternalConnection) {
    return /*#__PURE__*/_jsx(UITooltip, {
      title: tooltip,
      disabled: !tooltip,
      children: /*#__PURE__*/_jsx(UIButton, {
        "data-hs-analytics-id": getEventSelectorId('HEADER-CONNECT'),
        size: sticky ? 'small' : 'default',
        use: "primary",
        disabled: !!tooltip,
        external: standardConnection && !tooltip,
        href: isAppInstallable ? getFormattedUrl(appUrl) : null,
        onClick: onConnectButtonClick,
        role: "button",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: connectButtonKeyPath
        })
      })
    });
  }

  if (isDataSyncApp) {
    return /*#__PURE__*/_jsx(AsyncDataSyncConnectButton, {
      portalId: PortalIdParser.get(),
      appId: appId,
      "data-hs-analytics-id": getEventSelectorId('HEADER-CONNECT'),
      size: sticky ? 'small' : 'default',
      use: "primary",
      onClick: () => {
        handleTrackInteraction('click connect button');
      },
      trackingSource: "marketplace",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: connectButtonKeyPath
      })
    });
  } // Iframe modal button


  if (homemadeModalConnection) {
    return /*#__PURE__*/_jsxs(Fragment, {
      children: [/*#__PURE__*/_jsx(UIButton, {
        "data-hs-analytics-id": getEventSelectorId('HEADER-CONNECT'),
        size: sticky ? 'small' : 'default',
        use: "primary",
        onClick: () => {
          setOpenIframeModal(true);
          handleTrackInteraction('click connect button');
        },
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: connectButtonKeyPath
        })
      }), openIframeModal && /*#__PURE__*/_jsxs(UIModal, {
        width: 600,
        use: "success",
        children: [/*#__PURE__*/_jsxs(UIDialogHeader, {
          children: [/*#__PURE__*/_jsx(UIDialogCloseButton, {
            onClick: () => setOpenIframeModal(false)
          }), /*#__PURE__*/_jsx(H2, {
            "aria-level": 2,
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: `${keyPath}.modalTitle`
            })
          })]
        }), /*#__PURE__*/_jsx(UIDialogBody, {
          className: "p-bottom-4",
          children: /*#__PURE__*/_jsx(AsyncIntegrationsConnectModal, {
            portalId: PortalIdParser.get(),
            appId: appId,
            sourceAppId: "app-marketplace",
            text: I18n.text(connectButtonKeyPath),
            onConnectionComplete: handleConnectionComplete,
            onIntegrationNotFound: () => {
              console.log('not found');
            }
          })
        })]
      })]
    });
  } // Iframe button


  return /*#__PURE__*/_jsx(Fragment, {
    children: /*#__PURE__*/_jsx("div", {
      "data-hs-analytics-id": getEventSelectorId('HEADER-CONNECT'),
      style: {
        display: 'inline-block',
        verticalAlign: 'middle'
      },
      className: "m-left-3",
      children: /*#__PURE__*/_jsx(AsyncIntegrationsConnectButton, {
        portalId: PortalIdParser.get(),
        appId: appId,
        sourceAppId: "app-marketplace",
        text: I18n.text(connectButtonKeyPath),
        onClick: () => handleTrackInteraction('click connect button'),
        onConnectionComplete: handleConnectionComplete,
        onConnectionFailed: err => onConnectFailed(err.meta.type, appName)
      })
    })
  });
};

ConnectButton.propTypes = {
  appId: PropTypes.number,
  appName: PropTypes.string.isRequired,
  appUrl: PropTypes.string,
  connectionType: CustomPropTypes.connectionType.isRequired,
  installability: CustomPropTypes.installability.isRequired,
  isCustomer: PropTypes.bool,
  isDataSyncApp: PropTypes.bool,
  listingId: PropTypes.number.isRequired,
  onConnectButtonClick: PropTypes.func.isRequired,
  onConnectFailed: PropTypes.func.isRequired,
  onTrackEvent: PropTypes.func,
  onTrackInteraction: PropTypes.func.isRequired,
  onTrackPageview: PropTypes.func,
  sticky: PropTypes.bool
};
const mapDispatchToProps = {
  onConnectFailed: handleConnectError
};
export default connect(null, mapDispatchToProps)(ConnectButton);